import { $, $$, isMobile } from '@utils';
import removeAccentMarkAndUppercase from '@utils/RemoveAccentMarkAndUppercase';

function init() {
  addEventListeners();
}

function showOptions(listbox, listboxOptions, selectArrow) {
  if (listboxOptions.length <= 0) return;
  listbox.classList.remove('au-visually-hidden');

  const activeOption = Array.from(listboxOptions).find((opt) => opt.classList.contains('au-active'));
  (activeOption || listboxOptions[0]).focus();

  listboxOptions.forEach((listboxOption) => {
    if (listboxOption.getAttribute('data-au-listbox-option-disabled') === null) {
      listboxOption.setAttribute('tabindex', '0');
    }
    listboxAddTooltip(listboxOption);
  });
  selectArrow.classList.add('au-float-input__arrow--active');
  const select = listbox.closest('[data-au-select]');
  select.classList.add('au-select--active');
  select.querySelector('[data-au-select-main]').setAttribute('aria-expanded', 'true');
}

function hideOptions(listbox, listboxOptions, selectArrow, focusTarget) {
  if (listboxOptions.length <= 0) return;
  listbox.classList.add('au-visually-hidden');
  listboxOptions.forEach((listboxOption) => {
    listboxOption.setAttribute('tabindex', '-1');
    listboxRemoveTooltip(listboxOption);
  });
  selectArrow.classList.remove('au-float-input__arrow--active');
  const select = listbox.closest('[data-au-select]');
  select.classList.remove('au-select--active');
  select.querySelector('[data-au-select-main]').setAttribute('aria-expanded', 'false');
  if (focusTarget) focusTarget.focus();
}

function listboxSelection(select, listbox, selectArrow) {
  const listboxOptions = select.querySelectorAll('[data-au-listbox-option]');
  if (listboxOptions.length <= 0) return;

  const options = select.querySelectorAll('[data-au-option]');
  const selectMultiple = select.querySelector('select').hasAttribute('multiple');

  let typeaheadBuffer = '';
  let typeaheadTimer = null;
  const TYPEAHEAD_DELAY = 500;

  listboxOptions.forEach((listboxOption, index) => {
    // Avoid re-adding listeners
    if (listboxOption.dataset.listenerAttached === 'true') return;

    listboxOption.addEventListener('click', (e) => {
      const removeOption = listboxOption.closest('[data-au-select]').querySelector('[data-au-listbox-option-remove]');
      const isRemoveOption =
        e.target.closest('[data-au-listbox-option-remove]') || e.target.hasAttribute('data-au-listbox-option-remove');

      if (isRemoveOption) {
        e.preventDefault();
        clearSelect(select);
        selectForceFocusAndBlur(select.querySelector('select'));
      } else {
        e.stopPropagation();
        const listboxOptionId = listboxOption.id;
        const listboxOptionValue = listboxOption.querySelector('.au-select__listbox-text').textContent.trim();
        const optionSelected = $(`[data-au-option="${listboxOptionId}"]`);
        const listboxOptionIsActive = listboxOption.classList.contains('au-active');
        const selectText = select.querySelector('[data-au-select-text]');
        const currentSelectText = selectText.textContent.trim();

        // Enable "Deselect" option
        if (removeOption) {
          removeOption.classList.remove('disabled');
        }

        //SELECT NORMAL
        if (!selectMultiple) {
          // Print listbox option selected value in span
          selectText.textContent = listboxOptionValue;

          // Set all select options as not selected
          options.forEach((option) => {
            option.removeAttribute('selected');
            option.selected = false;
          });

          // Set select option selected
          optionSelected.setAttribute('selected', true);
          optionSelected.selected = true;

          // Remove active style from all listbox options and set aria-selected to false
          listboxOptions.forEach((listboxOption) => {
            listboxOption.classList.remove('au-active');
            if (listboxOption.hasAttribute('aria-selected')) {
              listboxOption.setAttribute('aria-selected', false);
            }
          });

          // Hide listbox when listbox option is selected
          hideOptions(listbox, listboxOptions, selectArrow, select.querySelector('[data-au-select-main]'));
        } else {
          //SELECT MULTIPLE
          // Print listbox option selected value in span
          if (currentSelectText.includes(listboxOptionValue)) {
            // Add listbox option selected value if is not present
            const updatedText = currentSelectText
              .split(', ')
              .filter((item) => item !== listboxOptionValue)
              .join(', ');

            selectText.textContent = updatedText;
          } else {
            // Remove listbox option selected value if is already present
            const updatedText = currentSelectText ? `${currentSelectText}, ${listboxOptionValue}` : listboxOptionValue;
            selectText.textContent = updatedText;
          }

          // Set select option selected
          // Add active style to listbox option selected and set aria-selected to true
          if (listboxOptionIsActive) {
            optionSelected.removeAttribute('selected');
            optionSelected.selected = false;
            listboxOption.classList.remove('au-active');
            listboxOption.setAttribute('aria-selected', false);
          } else {
            optionSelected.setAttribute('selected', true);
            optionSelected.selected = true;
            listboxOption.classList.add('au-active');
            listboxOption.setAttribute('aria-selected', true);
          }
        }

        // Add selected listbox option to aria-activedescendant in listbox
        listbox.setAttribute('aria-activedescendant', listboxOptionId);

        // Trigger form validators (bouncer listens to 'input') to re-evaluate
        const nativeSelect = select.querySelector('select');
        nativeSelect.dispatchEvent(new Event('input', { bubbles: true }));
        nativeSelect.dispatchEvent(new Event('change', { bubbles: true }));

        // Add tooltip to select if selected text overflows
        // first, if it exists, we remove it
        if (select.querySelector('[data-au-select-tooltip]')) {
          select.querySelector('[data-au-select-tooltip]').remove();
        }

        // create tooltip and append it to select only if text overflows and if it's not a mobile device
        if (!isMobile.matches && selectText.scrollWidth > selectText.offsetWidth) {
          const selectTooltip = document.createElement('span');
          selectTooltip.setAttribute('aria-hidden', true);
          selectTooltip.setAttribute('data-au-select-tooltip', '');
          selectTooltip.classList.add('au-select__tooltip');
          selectTooltip.textContent = selectText.innerText;

          select.appendChild(selectTooltip);

          selectText.addEventListener('mousemove', (e) => {
            selectTooltip.style.left = `${e.clientX - selectTooltip.offsetWidth / 2}px`;
            selectTooltip.style.top = `${e.clientY - 56}px`;

            selectTooltip.style.visibility = 'visible';
            selectTooltip.style.opacity = 1;
          });

          selectText.addEventListener('mouseleave', () => {
            selectTooltip.style.visibility = 'hidden';
            selectTooltip.style.opacity = 0;
          });
        }

        const shouldTrackModuleClick = !select.hasAttribute('data-au-world-map-others-select');
        if (shouldTrackModuleClick) {
          const selectLabel = removeAccentMarkAndUppercase(select.querySelector('label').innerText);
          window.dataLayer.push({
            event: 'navigation',
            content_type: 'clickModulo',
            item_id: `${selectLabel}_${removeAccentMarkAndUppercase(selectText.innerText.split(', ').join('_'))}`,
          });
        }
      }
    });

    // Handle Keyboard navigation
    listboxOption.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        const optionToFocus =
          listboxOptions[index + 1] && listboxOptions[index + 1].hasAttribute('data-au-listbox-option-disabled')
            ? listboxOptions[index + 2]
            : listboxOptions[index + 1];
        const nextListboxOption = optionToFocus || listboxOptions[0];

        nextListboxOption.focus();
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        const optionToFocus =
          listboxOptions[index - 1] && listboxOptions[index - 1].hasAttribute('data-au-listbox-option-disabled')
            ? listboxOptions[index - 2]
            : listboxOptions[index - 1];
        const prevListBoxOption = optionToFocus || listboxOptions[listboxOptions.length - 1];

        prevListBoxOption.focus();
      } else if (e.key === 'Enter') {
        e.preventDefault();
        listboxOption.click();
      } else if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
        e.preventDefault();
        clearTimeout(typeaheadTimer);
        typeaheadBuffer += e.key.toLowerCase();
        typeaheadTimer = setTimeout(() => {
          typeaheadBuffer = '';
        }, TYPEAHEAD_DELAY);

        const match = Array.from(listboxOptions).find(
          (opt) =>
            !opt.hasAttribute('data-au-listbox-option-disabled') &&
            !opt.hasAttribute('data-au-listbox-option-remove') &&
            opt
              .querySelector('[data-au-select-listbox-text]')
              .textContent.trim()
              .toLowerCase()
              .startsWith(typeaheadBuffer),
        );

        if (match) {
          match.focus();
        }
      }
    });

    listboxOption.addEventListener('blur', (e) => {
      if (!select.contains(e.relatedTarget)) {
        hideOptions(listbox, listboxOptions, selectArrow);
      }
    });

    // Set flag so listeners aren't added again
    listboxOption.dataset.listenerAttached = 'true';
  });
}

function clearSelect(select) {
  const listbox = select.querySelector('[data-au-listbox]');
  const listboxOptions = select.querySelectorAll('[data-au-listbox-option]');
  const selectText = select.querySelector('[data-au-select-text]');
  const options = select.querySelectorAll('[data-au-option]');
  const removeOption = select.querySelector('[data-au-listbox-option-remove]');

  // Disable "Deselect" option
  removeOption.classList.add('disabled');

  // Clear select text
  selectText.textContent = '';

  // Set all select options as not selected
  options.forEach((option) => {
    option.removeAttribute('selected');
  });

  // Remove active style from all listbox options and set aria-selected to false
  listboxOptions.forEach((listboxOption) => {
    listboxOption.classList.remove('au-active');
    if (listboxOption.hasAttribute('aria-selected')) {
      listboxOption.setAttribute('aria-selected', false);
    }
  });

  // Remove tooltip if exists
  if (select.querySelector('[data-au-select-tooltip]')) {
    select.querySelector('[data-au-select-tooltip]').remove();
  }

  // Hide listbox
  hideOptions(listbox, listboxOptions, select.querySelector('[data-au-float-input-arrow]'));
}

function displaySelect(select, listbox, selectArrow) {
  const isDisplayed = !listbox.classList.contains('au-visually-hidden');
  const listboxOptions = select.querySelectorAll('[data-au-listbox-option]');

  if (isDisplayed) {
    hideOptions(listbox, listboxOptions, selectArrow);
  } else {
    showOptions(listbox, listboxOptions, selectArrow);

    const closeOnOutsideClick = (e) => {
      if (!select.contains(e.target) && !listbox.contains(e.target)) {
        hideOptions(listbox, listboxOptions, selectArrow);
      }
      if (!e.target.closest('[data-au-form-element]') || listbox.contains(e.target)) {
        selectForceFocusAndBlur(select.querySelector('select'));
      }
    };

    const closeOnEscape = (e) => {
      if (e.key === 'Escape') {
        hideOptions(listbox, listboxOptions, selectArrow, select.querySelector('[data-au-select-main]'));
        document.removeEventListener('keydown', closeOnEscape);
        document.removeEventListener('click', closeOnOutsideClick);
      }
    };

    document.addEventListener('click', closeOnOutsideClick, { once: true });
    document.addEventListener('keydown', closeOnEscape);
  }
}

function listboxAddTooltip(listboxOption) {
  const listBoxText = listboxOption.querySelector('[data-au-select-listbox-text]');

  if (listBoxText.scrollWidth > listBoxText.offsetWidth) {
    const listBoxTooltip = document.createElement('span');
    listBoxTooltip.setAttribute('aria-hidden', true);
    listBoxTooltip.setAttribute('data-au-select-listbox-tooltip', '');
    listBoxTooltip.classList.add('au-select__listbox-tooltip');
    listBoxTooltip.textContent = listBoxText.innerText;

    listboxOption.appendChild(listBoxTooltip);

    listboxOption.addEventListener('mousemove', (e) => {
      listBoxTooltip.style.left = `${e.clientX - listBoxTooltip.offsetWidth / 2}px`;
      listBoxTooltip.style.top = `${e.clientY - 56}px`;

      listBoxTooltip.style.visibility = 'visible';
      listBoxTooltip.style.opacity = 1;
    });

    listboxOption.addEventListener('mouseleave', () => {
      listBoxTooltip.style.visibility = 'hidden';
      listBoxTooltip.style.opacity = 0;
    });
  }
}

function listboxRemoveTooltip(listboxOption) {
  if (listboxOption.closest('[data-au-select]').querySelector('[data-au-select-listbox-tooltip]')) {
    listboxOption.closest('[data-au-select]').querySelector('[data-au-select-listbox-tooltip]').remove();
  }
}

// function to force validation when we focusout a au-select element
function selectForceFocusAndBlur(el) {
  if (el.required) {
    el.focus();
    el.blur();
  }
}

function addEventListeners() {
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (mutation.type === 'childList') {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) {
            // Element node
            const selects = node.matches('[data-au-select]') ? [node] : node.querySelectorAll('[data-au-select]');

            selects.forEach((select) => initializeSelect(select));
          }
        });
      } else if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
        // Detect class changes
        if (mutation.target.matches('[data-au-select]')) {
          initializeSelect(mutation.target);
        }
      }
    });
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['class'],
  });

  // Initialize existing selects
  $$('[data-au-select]').forEach(initializeSelect);

  function initializeSelect(select) {
    if (select.dataset.selectInitialized === 'true') return;
    select.dataset.selectInitialized = 'true';

    const listbox = select.querySelector('[data-au-listbox]');
    const selectArrow = select.querySelector('[data-au-float-input-arrow]');
    const selectDisplayer = select.querySelector('[data-au-select-main]');
    const isSelectInline = select.dataset.auSelectInline === '';

    selectDisplayer.addEventListener('click', (e) => {
      e.preventDefault();
      displaySelect(select, listbox, selectArrow);
      listboxSelection(select, listbox, selectArrow);
    });

    selectDisplayer.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowDown' || e.key === ' ' || e.key === 'Enter') {
        e.preventDefault();
        displaySelect(select, listbox, selectArrow);
        listboxSelection(select, listbox, selectArrow);
      }
    });

    selectDisplayer.addEventListener('blur', (e) => {
      const selectEl = e.target.querySelector('select');
      const listBoxIshidden = e.target
        .closest('[data-au-select]')
        .querySelector('[data-au-listbox]')
        .classList.contains('au-visually-hidden');

      if (listBoxIshidden && selectEl.value === '') {
        selectForceFocusAndBlur(selectEl);
      }
    });

    if (isSelectInline) {
      listbox.style.left = `${selectDisplayer.offsetLeft}px`;
    }
  }
}

export default { init };
